import React, { useCallback, useState, useRef } from "react";
import { colors2, getVal, UI_CAPTION } from "oolib";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import TooltipV2 from "@/components/UI/Tooltip";
import { useGetAllOptionsForDimension2 } from "../utils/useGetAllOptionsForDimension2";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { CascadeFieldPicker } from "@/components/DataFieldInputSelector/CascadeFieldPicker";
import { useBarChartConfig } from "./ConfigForm_Context";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { DATA_KEYS } from "./DATA_KEYS";
import { CollapseTransition } from "../../utils/CollapseTransition";
import { NumberFieldConfig } from "./NumberFieldConfig";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { getHiddenDim2ForPreset } from "../utils/getTopBottomDatapoints";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

export const ConfigForm_Dimension2Section = () => {
  const {
    widgetConfig,
    dataKeys,
    setConfig,
    updateWidget,
    underEditWidgetIdx,
    dimensionsInputPropsConfig,
    dim1IsGroupQuestion,
    handleDimension2Change,
  } = useBarChartConfig();

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  const dim2Options = useGetAllOptionsForDimension2({
    dimension1Value: getVal(widgetConfig, dataKeys.dimension1),
  });

  const dim2Value = getVal(widgetConfig, dataKeys.dimension2);
  const isNumberInput = dim2Value?.comp === 'NumberInput';

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  // Show/Hide (eye) for non-number breakdown values; number fields get theirs
  // from NumberFieldConfig instead.
  const visibilityModifier = useVisibilityModifier({
    dimension: 2,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput,
    updateConfig,
    getHiddenForPreset: getHiddenDim2ForPreset,
  });

  if (!dim1IsGroupQuestion && !dim2Value) return;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
      <CollapseTransition isVisible={dim1IsGroupQuestion}>
        <div style={{ borderRadius: '6px', padding: '1rem', marginBottom: '0.4rem', backgroundColor: colors2.redBG }}>
          <UI_CAPTION style={{ color: colors2.red }}>
            Currently, if a question that was in grouped radio select format is selected, breakdown is not possible against it
          </UI_CAPTION>
        </div>
      </CollapseTransition>

      <CollapseTransition isVisible={!!dim2Value}>
        <DataFieldInputSelector
          options={dim2Options}
          value={dim2Value}
          onChange={(id, v) => handleDimension2Change(null, v)}
          actions={[{ icon: 'X', onClick: () => handleDimension2Change(null, null), hoverColor: 'red' }]}
          placeholder={dimensionsInputPropsConfig.input2?.label || "Breakdown by"}
          disabled={dim1IsGroupQuestion}
          modifier={visibilityModifier}
          picker={getPickerProp({ widgetComp: 'WidgetBarChart', slot: 'dimension2' })}
        />
      </CollapseTransition>

      <CollapseTransition isVisible={!!dim2Value}>
        <NumberFieldConfig
          dimensionKey="dimension2"
          dimensionValue={dim2Value}
          dataKeys={DATA_KEYS}
          thisWidgetConfig={widgetConfig}
          setConfig={setConfig}
          underEditWidgetIdx={underEditWidgetIdx}
        />
      </CollapseTransition>
    </div>
  );
};

/** "+" button for adding breakdown — exposed for ConfigForm/index.js headerRight */
export const Dimension2AddFieldButton = () => {
  const {
    widgetConfig,
    dataKeys,
    dim1IsGroupQuestion,
    handleDimension2Change,
  } = useBarChartConfig();

  const dim1Value = getVal(widgetConfig, dataKeys.dimension1);
  const dim2Value = getVal(widgetConfig, dataKeys.dimension2);

  const dim2Options = useGetAllOptionsForDimension2({ dimension1Value: dim1Value });

  const [showLightbox, setShowLightbox] = useState(false);
  const btnRef = useRef(null);

  const canAdd = !!dim1Value && !dim2Value && !dim1IsGroupQuestion;

  return (
    <>
      <TooltipV2
        text={
          !dim1Value
            ? "Select data first"
            : dim2Value
              ? "Breakdown already added"
              : dim1IsGroupQuestion
                ? "Breakdown not available for grouped questions"
                : "Add a breakdown dimension"
        }
        placement="top"
      >
        <span ref={btnRef}>
          <ButtonGhost
            icon="Plus"
            onClick={() => setShowLightbox(true)}
            disabled={!canAdd}
            style={{ padding: "4px" }}
          />
        </span>
      </TooltipV2>
      {showLightbox && (() => {
        // Shared select handler: enrich with categoryLabel (parity with the card's
        // handleFieldSelect) then commit + close.
        const handlePick = (selectedField) => {
          let enriched = selectedField;
          if (selectedField && !selectedField.categoryLabel) {
            const group = dim2Options.find(g =>
              Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
            );
            if (group) enriched = { ...selectedField, categoryLabel: group.display };
          }
          handleDimension2Change(null, enriched);
          setShowLightbox(false);
        };
        // The "+" add flow must match the dim-2 card (cascade), not the old flat popup.
        const cascade = getPickerProp({ widgetComp: 'WidgetBarChart', slot: 'dimension2' });
        return cascade ? (
          <CascadeFieldPicker
            anchorEl={btnRef.current}
            open
            onOpenChange={(v) => { if (!v) setShowLightbox(false); }}
            options={dim2Options}
            value={undefined}
            onChange={handlePick}
          />
        ) : (
          <SearchableLightbox
            anchorRef={btnRef}
            onClose={() => setShowLightbox(false)}
            options={dim2Options}
            grouped={dim2Options.some(item => item.options && Array.isArray(item.options))}
            value={undefined}
            onChange={(_, selectedField) => handlePick(selectedField)}
            searchPlaceholder="Search fields"
          />
        );
      })()}
    </>
  );
};
